import { AddCircleOutlined, AutoFixHighOutlined, CloudSyncOutlined, DeleteOutlined, FolderOpenOutlined, VisibilityOutlined } from "@mui/icons-material";
import { Box, FormControl, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { type Dispatch, type FC, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemSwitch } from "../../../../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemValidationTextField } from "../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { BrandMarkTile } from "../../../../../components/UIToolkit/molecules/BrandMarkTile";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import {
	BORDERLESS_INPUT_SX,
	CONFIG_TABLE_BODY_CELL_SX,
	CONFIG_TABLE_BODY_ROW_SX,
	CONFIG_TABLE_HEADER_CELL_SX,
} from "../../../../../components/UIToolkit/Tables/ConfigTable/configTableSx";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { useResyncIntegration } from "../../../../../hooks/useResyncIntegration";
import { INK, NEUTRAL, NUMERIC_FIELD_WIDTH } from "../../../../../theme/tokens";
import {
	addAdpDepartmentKey,
	composePaycomSftpDepartmentId,
	isValidPaycomSftpAlSuffix,
	splitPaycomSftpDepartmentId,
} from "../../../../../utils/hrisIntegrations";
import { ExternalSource, type HrisIntegrationInput, type HrisIntegrationType } from "../../../../../utils/models";
import { HRIS_SOURCES, HRIS_TYPES, hrisSourceOption, hrisSourceTileColors } from "../generalMetadataConsts";
import type { HrisSourceCapability } from "../sftpHooks";
import { useSftpDepartmentKeys } from "../sftpHooks";
import type { GeneralFormData } from "../types";
import { SftpFileBrowserDialog } from "./SftpFileBrowserDialog";
import { SftpFilePreviewDialog } from "./SftpFilePreviewDialog";

/** One row of `formData.hrisIntegrations` together with its index, which is how it is written back. */
export type IndexedIntegration = { integration: HrisIntegrationInput; index: number };

type Props = {
	source: ExternalSource;
	/** Every feed configured for this source, in form order. */
	rows: IndexedIntegration[];
	/**
	 * What this source needs, straight off its syncher.
	 *
	 * `undefined` means the lookup failed — NOT "nothing". The card then shows every field and
	 * withholds the engineering warning, because wrongly telling an operator to escalate a source they
	 * can configure here, or hiding an identifier the sync actually consumes, are both worse than
	 * showing one field too many.
	 */
	capability: HrisSourceCapability | undefined;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
	isDirty: boolean;
	isSupport: boolean;
};

const toOption = (value: string): SystemAutoCompleteOption<string> => ({ label: value, value });

/**
 * One HRIS source and every feed it delivers.
 *
 * DAT-773: the Dept ID and Network ID live HERE, once per source, rather than on each feed — they
 * identify the department inside the vendor's system, which cannot differ between that vendor's
 * employees file and its timesheet. Editing either writes it onto every one of the source's rows,
 * because the columns are still per-row and the backend refuses a source whose rows disagree.
 */
export const HrisSourceCard: FC<Props> = ({ source, rows, capability, setFormData, isDirty, isSupport }) => {
	// Both default to "show it" when the capability is unknown — see the prop's docstring.
	const fileBased = capability?.file_based;
	// Dept ID is unconditional: hris.hris_department_id is NOT NULL and every source routes on it.
	// Network ID is asked for only when the source's syncher actually reads it (Workday, iSolved, AOD).
	const showNetworkId = capability?.requires_network_id !== false;
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();

	const first = rows[0].integration;
	const sourceOption = hrisSourceOption(source);
	const sourceTileColors = hrisSourceTileColors(source);
	const isPaycomSftp = source === ExternalSource.PaycomSftp;
	const isAdp = source === ExternalSource.Adp;
	const paycomDepartmentIdParts = splitPaycomSftpDepartmentId(first.hris_department_id);
	const displayedDepartmentId = isPaycomSftp ? paycomDepartmentIdParts.baseDepartmentId : first.hris_department_id;
	const [paycomBaseDepartmentIdInput, setPaycomBaseDepartmentIdInput] = useState(paycomDepartmentIdParts.baseDepartmentId);
	const [paycomAlSuffixInput, setPaycomAlSuffixInput] = useState(paycomDepartmentIdParts.alSuffix);
	const [hasRejectedPaycomAlSuffix, setHasRejectedPaycomAlSuffix] = useState(false);

	useEffect(() => {
		const parts = splitPaycomSftpDepartmentId(first.hris_department_id);
		setPaycomBaseDepartmentIdInput(isPaycomSftp ? parts.baseDepartmentId : "");
		setPaycomAlSuffixInput(isPaycomSftp ? parts.alSuffix : "");
		setHasRejectedPaycomAlSuffix(false);
	}, [first.hris_department_id, isPaycomSftp]);

	// The feed whose file the Dept ID codes are derived from: the first one that is actually connected.
	// A source's files all carry the same department column, so any connected one answers the question.
	const keySourceRow = rows.find(({ integration }) => integration.folder && integration.file)?.integration;
	const [hasOpenedDeptIdPicker, setHasOpenedDeptIdPicker] = useState(false);
	const {
		data: departmentKeys,
		isFetching: isFetchingDepartmentKeys,
		error: departmentKeysError,
	} = useSftpDepartmentKeys({
		folder: keySourceRow?.folder,
		file: keySourceRow?.file,
		source,
		type: keySourceRow?.type ?? first.type,
		enabled: isSupport && hasOpenedDeptIdPicker,
	});

	useEffect(() => {
		if (!departmentKeysError) return;
		trackError("fetch_sftp_department_keys", {
			error_message: departmentKeysError instanceof Error ? departmentKeysError.message : String(departmentKeysError),
			department_id: chosenDepartment.department_id,
			source,
		});
	}, [departmentKeysError, trackError, chosenDepartment.department_id, source]);

	// The count rides in the label so the shared autocomplete can render a plain, compact option.
	const departmentKeyOptions = useMemo<SystemAutoCompleteOption<string>[]>(
		() => departmentKeys?.keys.map((key) => ({ label: `${key.key}${key.label ? ` - ${key.label}` : ""} (${key.employee_count})`, value: key.key })) ?? [],
		[departmentKeys],
	);

	/** The one code the connected file carries, when there is exactly one — what "auto-fill" fills. */
	const soleDepartmentKey = departmentKeys?.keys.length === 1 ? departmentKeys.keys[0].key : undefined;

	const patchRows = useCallback(
		(patch: Partial<HrisIntegrationInput>, indices: number[]) =>
			setFormData((previous) => ({
				...previous,
				hrisIntegrations: previous.hrisIntegrations.map((integration, index) => (indices.includes(index) ? { ...integration, ...patch } : integration)),
			})),
		[setFormData],
	);

	const groupIndices = useMemo(() => rows.map(({ index }) => index), [rows]);
	// Applied to the whole group, never to one row: see the component docstring.
	const patchSourceIds = useCallback((patch: Partial<HrisIntegrationInput>) => patchRows(patch, groupIndices), [groupIndices, patchRows]);
	const malformedPaycomRowIndices = useMemo(
		() =>
			isPaycomSftp
				? rows
						.filter(({ integration }) => {
							const parts = splitPaycomSftpDepartmentId(integration.hris_department_id);
							return parts.baseDepartmentId === "" && parts.alSuffix !== "";
						})
						.map(({ index }) => index)
				: [],
		[isPaycomSftp, rows],
	);
	const validPaycomDepartmentIds = useMemo(
		() =>
			isPaycomSftp
				? Array.from(
						new Set(
							rows
								.map(({ integration }) => integration.hris_department_id)
								.filter((departmentId) => {
									const parts = splitPaycomSftpDepartmentId(departmentId);
									return departmentId !== "" && !(parts.baseDepartmentId === "" && parts.alSuffix !== "");
								}),
						),
					)
				: [],
		[isPaycomSftp, rows],
	);
	const soleValidPaycomDepartmentId = validPaycomDepartmentIds.length === 1 ? validPaycomDepartmentIds[0] : undefined;
	const paycomConflictNeedsExplicitChoice = isPaycomSftp && validPaycomDepartmentIds.length > 1;
	const conflictDepartmentId = soleValidPaycomDepartmentId === undefined ? first.hris_department_id : soleValidPaycomDepartmentId;

	useEffect(() => {
		if (malformedPaycomRowIndices.length === 0) return;
		// A suffix without a base can never route. Clear only the malformed legacy rows so a valid,
		// conflicting row remains visible for the operator to resolve instead of being overwritten.
		if (malformedPaycomRowIndices.includes(rows[0].index)) {
			setPaycomBaseDepartmentIdInput("");
			setPaycomAlSuffixInput("");
		}
		patchRows({ hris_department_id: "" }, malformedPaycomRowIndices);
	}, [malformedPaycomRowIndices, patchRows, rows]);

	const removeRow = (index: number) =>
		setFormData((previous) => ({ ...previous, hrisIntegrations: previous.hrisIntegrations.filter((_, position) => position !== index) }));

	// A source may deliver SEVERAL feeds of one type, one per file — Empeon splits time off across
	// Previous/Current/Next Month, Paycom SFTP splits employees across Directory/Dates/Rate History. The
	// table's key is (department, source, type, folder, file) and the synchers read those rows as a set
	// (get_hris_integrations_list_map), so a feed's identity is its FILE, not its type. Nothing here may
	// cap rows per type: doing so made a normal Empeon or Lionstone department unconfigurable.
	const nextType = HRIS_TYPES.find(({ value }) => !rows.some(({ integration }) => integration.type === value))?.value ?? HRIS_TYPES[0].value;

	const addFeed = () => {
		setFormData((previous) => ({
			...previous,
			hrisIntegrations: [
				...previous.hrisIntegrations,
				{
					source,
					// Defaults to a type this source has no feed for yet, else the first — purely a
					// starting point the operator changes, never a restriction.
					type: nextType,
					hris_department_id: first.hris_department_id,
					external_network_id: first.external_network_id,
					active: true,
				},
			],
		}));
	};

	// Two rows of one source pointing at the SAME file are the only genuine duplicate — that is the
	// table's unique constraint, so it is what the UI flags.
	const duplicateFeedKeys = new Set(
		rows
			.map(({ integration }) => `${integration.type}|${integration.folder ?? ""}|${integration.file ?? ""}`)
			.filter((key, index, all) => all.indexOf(key) !== index),
	);

	// Legacy rows can disagree (the columns were per-feed before DAT-773). Surfaced with a one-click
	// fix rather than silently normalized on load, so the operator sees which value is being kept.
	const hasConflictingIds =
		new Set(rows.map(({ integration }) => integration.hris_department_id)).size > 1 ||
		(showNetworkId && new Set(rows.map(({ integration }) => integration.external_network_id ?? "")).size > 1);

	return (
		<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: NEUTRAL[300] }} data-testid={`hris-source-card-${source}`}>
			<Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
				<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start", width: { xs: "100%", md: "auto" } }}>
					{/* Mark and name are ONE object — a tighter gap than the header's, so the tile reads as the
					    card's identity rather than as a control sitting beside the picker. Same mark and hue
					    as the row this source was picked from, so a configured card is recognised as that
					    vendor before its name is read. */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
						<BrandMarkTile
							name={sourceOption?.label ?? source}
							mark={sourceOption?.mark}
							size="sm"
							color={sourceTileColors?.color}
							tint={sourceTileColors?.tint}
							dataTestId={`hris-source-mark-${source}`}
						/>
						<FormControl size="small" sx={{ minWidth: 180 }}>
							<Select
								value={source}
								onChange={(event) => patchSourceIds({ source: event.target.value as ExternalSource })}
								data-testid={`hris-source-select-${source}`}
							>
								{HRIS_SOURCES.map(({ value, label }) => (
									<MenuItem key={value} value={value}>
										{label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
					<Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
						<Box sx={{ width: { xs: "100%", sm: 200 } }}>
							<SystemAutoComplete<string>
								freeSolo
								fullWidth
								loading={isFetchingDepartmentKeys}
								options={departmentKeyOptions}
								onOpenStateChange={(_event, _reason, isOpen) => {
									if (isOpen) setHasOpenedDeptIdPicker(true);
								}}
								// Paycom SFTP presents the composite ID as two fields; every other source still
								// shows the one raw value exactly as it is stored.
								value={displayedDepartmentId ? toOption(displayedDepartmentId) : null}
								inputValue={isPaycomSftp ? paycomBaseDepartmentIdInput : undefined}
								onInputChange={
									isPaycomSftp
										? (_event, value, reason) => {
												if (reason === "input") setPaycomBaseDepartmentIdInput(value);
											}
										: undefined
								}
								onChange={(_event, option) => {
									const selectedValue = option === null ? "" : option.value;
									const selectedFromFile = !!option && departmentKeys?.keys.some((key) => key.key === option.value) === true;
									let nextDepartmentId = selectedValue;
									if (isPaycomSftp) {
										const selectedParts = splitPaycomSftpDepartmentId(selectedValue);
										setHasRejectedPaycomAlSuffix(false);
										if (selectedValue === "" || (selectedParts.baseDepartmentId === "" && selectedParts.alSuffix !== "")) {
											setPaycomBaseDepartmentIdInput("");
											setPaycomAlSuffixInput("");
											nextDepartmentId = "";
										} else if (selectedFromFile || selectedParts.alSuffix !== "") {
											setPaycomBaseDepartmentIdInput(selectedParts.baseDepartmentId);
											setPaycomAlSuffixInput(selectedParts.alSuffix);
										} else {
											setPaycomBaseDepartmentIdInput(selectedValue);
											nextDepartmentId = composePaycomSftpDepartmentId(selectedValue, paycomAlSuffixInput);
										}
									}
									if (isAdp && selectedFromFile) nextDepartmentId = addAdpDepartmentKey(first.hris_department_id, selectedValue);
									patchSourceIds({ hris_department_id: nextDepartmentId });
									track("dept_config_hris_dept_id_selected", {
										department_id: chosenDepartment.department_id,
										source,
										picked_from_file: selectedFromFile,
									});
								}}
								getOptionDataTestId={(option) => `hris-source-dept-id-option-${option.value}`}
								dataTestId={`hris-source-dept-id-${source}`}
								renderInput={(params) => (
									<TextField
										{...params}
										size="small"
										label={t("department.config.integrations.hris.col.deptId")}
										placeholder={t("department.config.integrations.hris.deptId.placeholder")}
										sx={{ width: "100%" }}
									/>
								)}
							/>
						</Box>
						{isPaycomSftp && (
							<>
								<SystemValidationTextField
									label={t("department.config.integrations.hris.col.alSuffix")}
									placeholder={t("department.config.integrations.hris.alSuffix.placeholder")}
									value={paycomAlSuffixInput}
									disabled={paycomDepartmentIdParts.baseDepartmentId === ""}
									onKeyDown={(event) => {
										if (event.key === "Enter") setHasRejectedPaycomAlSuffix(false);
									}}
									onChange={(event) => {
										const nextSuffix = event.target.value;
										if (!isValidPaycomSftpAlSuffix(nextSuffix)) {
											setHasRejectedPaycomAlSuffix(true);
											return;
										}
										setHasRejectedPaycomAlSuffix(false);
										setPaycomAlSuffixInput(nextSuffix);
										patchSourceIds({ hris_department_id: composePaycomSftpDepartmentId(paycomDepartmentIdParts.baseDepartmentId, nextSuffix) });
									}}
									validation={{
										validationCallback: () => !hasRejectedPaycomAlSuffix,
										helperText: { invalid: t("department.config.integrations.hris.alSuffix.invalid") },
										performValidation: "always",
										showValidWhenValid: "never",
									}}
									reserveHeightForHelperText={false}
									formControlSx={{ width: { xs: "100%", sm: NUMERIC_FIELD_WIDTH.SETTINGS } }}
									injectedProps={{
										onBlur: () => setHasRejectedPaycomAlSuffix(false),
										slotProps: { htmlInput: { inputMode: "numeric", pattern: "[0-9]*", "aria-invalid": hasRejectedPaycomAlSuffix } },
									}}
									dataTestId={`hris-source-al-suffix-${source}`}
								/>
								{hasRejectedPaycomAlSuffix && (
									<Box component="span" role="alert" sx={visuallyHidden}>
										{t("department.config.integrations.hris.alSuffix.invalid")}
									</Box>
								)}
							</>
						)}
						<SystemButton
							buttonVariant="borderless"
							size="small"
							leadingIcon={<AutoFixHighOutlined fontSize="small" />}
							tooltip={
								soleDepartmentKey
									? t("department.config.integrations.hris.deptId.autofill.tooltip", { key: soleDepartmentKey })
									: t("department.config.integrations.hris.deptId.autofill.unavailable")
							}
							aria-label={t("department.config.integrations.hris.deptId.autofill")}
							// Only when the file carries exactly ONE code: with several, "auto" would be a
							// guess about which facility this department is, and the dropdown already lists
							// them with their headcounts.
							disabled={!soleDepartmentKey || soleDepartmentKey === first.hris_department_id}
							data-testid={`hris-source-dept-id-autofill-${source}`}
							onClick={() => {
								if (!soleDepartmentKey) return;
								if (isPaycomSftp) {
									setPaycomAlSuffixInput(splitPaycomSftpDepartmentId(soleDepartmentKey).alSuffix);
									setHasRejectedPaycomAlSuffix(false);
								}
								patchSourceIds({ hris_department_id: soleDepartmentKey });
								track("dept_config_hris_dept_id_autofilled", { department_id: chosenDepartment.department_id, source });
							}}
						/>
					</Box>
					{showNetworkId && (
						<TextField
							size="small"
							label={t("department.config.integrations.hris.col.networkId")}
							placeholder={t("department.config.integrations.hris.networkId.placeholder")}
							value={first.external_network_id ?? ""}
							onChange={(event) => patchSourceIds({ external_network_id: event.target.value || undefined })}
							sx={{ width: 180 }}
							slotProps={{ htmlInput: { "data-testid": `hris-source-network-id-${source}` } }}
						/>
					)}
				</Box>
				<SystemButton
					buttonVariant="borderless"
					size="small"
					leadingIcon={<DeleteOutlined fontSize="small" />}
					tooltip={t("department.config.integrations.hris.source.delete")}
					aria-label={t("department.config.integrations.hris.source.delete")}
					data-testid={`hris-source-delete-${source}`}
					onClick={() =>
						setFormData((previous) => ({ ...previous, hrisIntegrations: previous.hrisIntegrations.filter((_, index) => !groupIndices.includes(index)) }))
					}
				/>
			</Box>

			{fileBased === false && (
				<SystemBanner
					tone="warning"
					title={t("department.config.integrations.hris.apiSource.title")}
					description={t("department.config.integrations.hris.apiSource.description")}
					sx={{ mt: 1.5 }}
					dataTestId={`hris-source-api-warning-${source}`}
				/>
			)}

			{hasConflictingIds && (
				<SystemBanner
					tone="warning"
					title={t("department.config.integrations.hris.idConflict.title")}
					description={
						paycomConflictNeedsExplicitChoice
							? t("department.config.integrations.hris.idConflict.chooseDescription")
							: t("department.config.integrations.hris.idConflict.description", {
									deptId: conflictDepartmentId,
									networkId: first.external_network_id ?? "—",
								})
					}
					action={
						paycomConflictNeedsExplicitChoice ? (
							<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
								{validPaycomDepartmentIds.map((departmentId) => (
									<SystemButton
										key={departmentId}
										buttonVariant="secondary"
										size="small"
										onClick={() => {
											patchSourceIds({ hris_department_id: departmentId });
											track("dept_config_hris_dept_id_conflict_resolved", {
												department_id: chosenDepartment.department_id,
												source,
												chosen_hris_department_id: departmentId,
											});
										}}
										data-testid={`hris-source-unify-ids-${source}-${departmentId}`}
									>
										{t("department.config.integrations.hris.idConflict.use", { deptId: departmentId })}
									</SystemButton>
								))}
							</Box>
						) : (
							<SystemButton
								buttonVariant="secondary"
								size="small"
								onClick={() => patchSourceIds({ hris_department_id: conflictDepartmentId, external_network_id: first.external_network_id })}
								data-testid={`hris-source-unify-ids-${source}`}
							>
								{t("department.config.integrations.hris.idConflict.apply")}
							</SystemButton>
						)
					}
					sx={{ mt: 1.5 }}
					dataTestId={`hris-source-id-conflict-${source}`}
				/>
			)}

			<Table size="small" sx={{ mt: 1.5, tableLayout: "fixed" }} aria-label={t("department.config.integrations.hris.types", { source })}>
				<TableHead>
					<TableRow>
						<TableCell align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: "22%" }]}>
							{t("department.config.integrations.hris.col.type")}
						</TableCell>
						{fileBased !== false && (
							<TableCell align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: "50%" }]}>
								{t("department.config.integrations.hris.col.file")}
							</TableCell>
						)}
						<TableCell align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: "12%" }]}>
							{t("department.config.integrations.hris.col.active")}
						</TableCell>
						<TableCell align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: "16%" }]} />
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map(({ integration, index }) => (
						<HrisTypeRow
							key={`${integration.type}-${index}`}
							integration={integration}
							index={index}
							fileBased={fileBased !== false}
							isDuplicate={duplicateFeedKeys.has(`${integration.type}|${integration.folder ?? ""}|${integration.file ?? ""}`)}
							isDirty={isDirty}
							isSupport={isSupport}
							onPatch={(patch) => patchRows(patch, [index])}
							onRemove={() => removeRow(index)}
						/>
					))}
				</TableBody>
			</Table>

			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
				<SystemButton
					buttonVariant="borderless"
					size="small"
					leadingIcon={<AddCircleOutlined fontSize="small" />}
					onClick={addFeed}
					data-testid={`hris-source-add-type-${source}`}
				>
					{t("department.config.integrations.hris.addType")}
				</SystemButton>
			</Box>
		</Paper>
	);
};

const HrisTypeRow: FC<{
	integration: HrisIntegrationInput;
	index: number;
	fileBased: boolean;
	/** Another feed of this source already points at the same type + file — the table's unique key. */
	isDuplicate: boolean;
	isDirty: boolean;
	isSupport: boolean;
	onPatch: (patch: Partial<HrisIntegrationInput>) => void;
	onRemove: () => void;
}> = ({ integration, index, fileBased, isDuplicate, isDirty, isSupport, onPatch, onRemove }) => {
	const { t } = useTranslation();
	const resyncIntegration = useResyncIntegration();
	const [browserOpen, setBrowserOpen] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const isConnected = !!integration.folder && !!integration.file;

	return (
		<TableRow sx={CONFIG_TABLE_BODY_ROW_SX}>
			<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
				<FormControl size="small" fullWidth sx={BORDERLESS_INPUT_SX}>
					<Select
						value={integration.type}
						onChange={(event) => onPatch({ type: event.target.value as HrisIntegrationType })}
						// A duplicate would be rejected by the table's unique constraint on save, so it is
						// flagged where it is created rather than surfaced as a 500 from the write.
						error={isDuplicate}
						data-testid={`hris-integration-type-${index}`}
					>
						{HRIS_TYPES.map(({ value, label }) => (
							<MenuItem key={value} value={value}>
								{label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</TableCell>
			{fileBased && (
				<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
						{/* A button, not a text field: the path is chosen by browsing the bucket, and a
						    free-text folder/file pair is exactly what made this configuration engineering's job.
						    Its own `tooltip` prop, never an outer <Tooltip> — SystemButton doesn't spread
						    arbitrary props onto the element it forwards its ref to, so wrapping it trips MUI's
						    "children not forwarding props" warning and the tooltip never anchors. */}
						<SystemButton
							buttonVariant="secondary"
							size="small"
							leadingIcon={<FolderOpenOutlined fontSize="small" />}
							tooltip={isConnected ? `${integration.folder}/${integration.file}` : undefined}
							onClick={() => setBrowserOpen(true)}
							sx={{ justifyContent: "flex-start", flex: 1, minWidth: 0, overflow: "hidden" }}
							data-testid={`hris-integration-file-${index}`}
						>
							<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: isConnected ? undefined : INK.MUTED }}>
								{isConnected ? integration.file : t("department.config.integrations.sftp.selectFile")}
							</Box>
						</SystemButton>
						<SystemButton
							buttonVariant="borderless"
							size="small"
							leadingIcon={<VisibilityOutlined fontSize="small" />}
							disabled={!isConnected}
							tooltip={isConnected ? t("department.config.integrations.sftp.browser.preview") : t("department.config.integrations.sftp.preview.needsFile")}
							aria-label={t("department.config.integrations.sftp.browser.preview")}
							onClick={() => setPreviewOpen(true)}
							data-testid={`hris-integration-preview-${index}`}
						/>
					</Box>
					<SftpFileBrowserDialog
						open={browserOpen}
						folder={integration.folder}
						file={integration.file}
						source={integration.source}
						type={integration.type}
						onClose={() => setBrowserOpen(false)}
						onSelect={(folder, file) => onPatch({ folder, file })}
					/>
					{previewOpen && isConnected && (
						<SftpFilePreviewDialog
							open
							folder={integration.folder as string}
							file={integration.file as string}
							source={integration.source}
							type={integration.type}
							onClose={() => setPreviewOpen(false)}
						/>
					)}
				</TableCell>
			)}
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { verticalAlign: "middle" }]}>
				<SystemSwitch
					checked={integration.active}
					onChange={(event) => onPatch({ active: event.target.checked })}
					dataTestId={`hris-integration-active-${index}`}
				/>
			</TableCell>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { verticalAlign: "middle", whiteSpace: "nowrap" }]}>
				{isSupport && (
					<SystemButton
						buttonVariant="borderless"
						size="small"
						leadingIcon={<CloudSyncOutlined fontSize="small" />}
						// Disabled while the form has unsaved edits (a re-sync would run against stale
						// config), while a re-sync is in flight, or when the feed is paused.
						disabled={isDirty || resyncIntegration.isPending || !integration.active}
						tooltip={!integration.active ? t("integration.resync.inactive") : isDirty ? t("integration.resync.dirty") : t("integration.resync.tooltip")}
						aria-label={t("integration.resync.tooltip")}
						data-testid={`hris-integration-resync-${index}`}
						onClick={() => resyncIntegration.mutate({ source: integration.source, type: integration.type, folder: integration.folder, file: integration.file })}
					/>
				)}
				<SystemButton
					buttonVariant="borderless"
					size="small"
					leadingIcon={<DeleteOutlined fontSize="small" />}
					tooltip={t("department.config.integrations.delete")}
					aria-label={t("department.config.integrations.delete")}
					data-testid={`hris-integration-delete-${index}`}
					onClick={onRemove}
				/>
			</TableCell>
		</TableRow>
	);
};
